import React from 'react';

import { Card, Empty, Popover, Typography } from 'antd';

import { EMPTY_MESSAGES, OWNER_AVATAR_SIZES } from '@/shared/lib/constants';
import { DomainTypeEditPopover } from '@/shared/ui/DomainTypeEditPopover';
import { ExpertEditPopover } from '@/shared/ui/ExpertEditPopover';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { OwnerAvatars } from '@/shared/ui/OwnerAvatars';
import { OwnerEditPopover } from '@/shared/ui/OwnerEditPopover';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import type { DomainItem } from '@/entities/domain';

import { useDomainExpertEdit, useDomainOwnerEdit, useDomainTypeEdit } from '@/features/domain/model';

import styles from './DomainDetailSidebar.module.css';

const { Title } = Typography;

type DomainDetailSidebarProps = {
  domainDetail: DomainItem;
};

export const DomainDetailSidebar: React.FC<DomainDetailSidebarProps> = ({ domainDetail }) => {
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const owners = domainDetail.owners || [];
  const experts = domainDetail.experts || [];

  const ownerEdit = useDomainOwnerEdit({
    domainId: domainDetail.id,
    domainFqn: domainDetail.fullyQualifiedName,
  });

  const expertEdit = useDomainExpertEdit({
    domainId: domainDetail.id,
    domainFqn: domainDetail.fullyQualifiedName,
  });

  const domainTypeEdit = useDomainTypeEdit({
    domainId: domainDetail.id,
    domainFqn: domainDetail.fullyQualifiedName,
    currentDomainType: domainDetail.domainType,
  });

  const renderOwnerDisplay = () => {
    if (owners.length === 0) {
      return <Empty description={EMPTY_MESSAGES.NO_OWNERS} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    const teamOwner = owners.find((owner) => owner.type === 'team');
    if (teamOwner) {
      const displayName = teamOwner.displayName || teamOwner.name;
      return (
        <div className={styles.ownerDisplayContent}>
          <Icon name="users" size={14} />
          <span>{displayName}</span>
        </div>
      );
    }

    return (
      <OwnerAvatars
        owners={owners.map((owner) => ({
          id: owner.id,
          name: owner.name,
          displayName: owner.displayName || owner.name,
          type: owner.type,
        }))}
        size={OWNER_AVATAR_SIZES.DETAIL}
        showName={true}
      />
    );
  };

  return (
    <div className={styles.sidebar}>
      {showOwners && (
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <Title level={5} className={styles.cardTitle}>
              소유자
            </Title>
            <Popover
              content={<OwnerEditPopover ownerEdit={ownerEdit} />}
              title="소유자 편집"
              trigger="click"
              open={ownerEdit.isOpen}
              onOpenChange={(visible) => (visible ? ownerEdit.handleOpen() : ownerEdit.handleClose())}
              placement="bottomLeft">
              <IconButton size="small" tooltip="소유자 편집">
                <Icon name="edit" size={16} />
              </IconButton>
            </Popover>
          </div>
          {renderOwnerDisplay()}
        </Card>
      )}
      {showOwners && (
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <Title level={5} className={styles.cardTitle}>
              전문가
            </Title>
            <Popover
              content={<ExpertEditPopover expertEdit={expertEdit} />}
              title="전문가 편집"
              trigger="click"
              open={expertEdit.isOpen}
              onOpenChange={(visible) => (visible ? expertEdit.handleOpen() : expertEdit.handleClose())}
              placement="bottomLeft">
              <IconButton size="small" tooltip="전문가 편집">
                <Icon name="edit" size={16} />
              </IconButton>
            </Popover>
          </div>
          {experts && experts.length > 0 ? (
            <OwnerAvatars
              owners={experts.map((expert) => ({
                id: expert.id,
                name: expert.name,
                displayName: expert.displayName || expert.name,
                type: expert.type,
              }))}
              size={OWNER_AVATAR_SIZES.DETAIL}
              showName={true}
            />
          ) : (
            <Empty description={EMPTY_MESSAGES.NO_EXPERTS} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>
      )}

      <Card className={styles.card}>
        <div className={styles.cardHeader}>
          <Title level={5} className={styles.cardTitle}>
            도메인 유형
          </Title>
          <Popover
            content={<DomainTypeEditPopover domainTypeEdit={domainTypeEdit} />}
            trigger="click"
            open={domainTypeEdit.isOpen}
            onOpenChange={(visible) => (visible ? domainTypeEdit.handleOpen() : domainTypeEdit.handleClose())}
            placement="bottomLeft"
            classNames={{ body: styles.domainTypePopover }}>
            <button type="button" className={styles.domainTypeButton} data-open={domainTypeEdit.isOpen}>
              <span className={styles.domainTypeText}>{domainDetail.domainType}</span>
              <div className={styles.arrow} />
            </button>
          </Popover>
        </div>
      </Card>
    </div>
  );
};
